import { gql } from 'graphql-request';
import { PortfolioSiteMetric } from 'src/shared/types/graph/portfolio';

const siteTableRowMetricsFragment = `
    metrics {
        name
        label
        frameworks
        ... on MultiMetricSingleYearSiteMetric {
            year
            metrics
        }
        ... on SingleYearSiteMetric {
            year
            data
        }
        ... on MultiYearSiteMetric {
            years
            datum
        }
        ... on MultiYearMetricSingleYearSiteMetric {
            year
            years
            metricWithTrend
        }
    }
`;

export const portfolioMapFiltersQuery = gql`
    query getPortfolioMapFilters($customerId: String!) {
        portfolio {
            getPortfolioFilters(customerId: $customerId) {
                availableMetrics
            }
        }
    }
`;

export type PortfolioMapFiltersQueryResult = {
    portfolio: {
        getPortfolioFilters: {
            availableMetrics: string[];
        };
    };
};

// --- New purpose-built endpoints ---

// Selection sets shared by each query and its grid-backed twin below. The two differ
// only in which field they call, so sharing the selection is what keeps them from
// drifting while both exist. They stop being pairs at DD-5858, when the
// projection-backed halves go.
const MAP_MARKER_FIELDS = `
    markers {
        siteId
        name
        coordinates
        metricValue
        speciesRedListCount
        speciesInvasiveCount
        hasEdna
        hasHabitatInsights
    }
    totalInBounds
    totalOverall
`;

export const mapMarkersQuery = gql`
    query getMapMarkers(
        $customerId: String!
        $metric: HlcaMetricType
        $bounds: BoundsInput
        $filters: [CustomFilterOptionInput]
        $pagination: PaginationInput
        $sortBy: SortByInput
    ) {
        portfolio {
            getMapMarkers(
                customerId: $customerId
                metric: $metric
                bounds: $bounds
                filters: $filters
                pagination: $pagination
                sortBy: $sortBy
            ) {
                ${MAP_MARKER_FIELDS}
            }
        }
    }
`;

// Grid-backed twin of mapMarkersQuery. Same shape, same variables; the species
// counts come from the prediction grid rather than the Neo4j projection.
export const nextMapMarkersQuery = gql`
    query getNextMapMarkers(
        $customerId: String!
        $metric: HlcaMetricType
        $bounds: BoundsInput
        $filters: [CustomFilterOptionInput]
        $pagination: PaginationInput
        $sortBy: SortByInput
    ) {
        portfolio {
            getNextMapMarkers(
                customerId: $customerId
                metric: $metric
                bounds: $bounds
                filters: $filters
                pagination: $pagination
                sortBy: $sortBy
            ) {
                ${MAP_MARKER_FIELDS}
            }
        }
    }
`;

export type NextMapMarkersQueryResult = {
    portfolio: {
        getNextMapMarkers: MapMarkersResponse;
    };
};

export type MapMarker = {
    siteId: string;
    name: string;
    coordinates: number[];
    metricValue: number | null;
    speciesRedListCount: number | null;
    speciesInvasiveCount: number | null;
    hasEdna: boolean;
    hasHabitatInsights: boolean;
};

export type MapMarkersResponse = {
    markers: MapMarker[];
    totalInBounds: number;
    totalOverall: number;
};

export type MapMarkersQueryResult = {
    portfolio: {
        getMapMarkers: MapMarkersResponse;
    };
};

const SITE_TABLE_FIELDS = `
    rows {
        id
        name
        area
        country
        metricValue
        ${siteTableRowMetricsFragment}
        speciesRedListCount
        speciesInvasiveCount
        hasEdna
        hasHabitatInsights
    }
    pagination {
        page
        pageSize
        total
    }
`;

export const siteTableQuery = gql`
    query getSiteTable(
        $customerId: String!
        $metric: HlcaMetricType
        $filters: [CustomFilterOptionInput]
        $pagination: PaginationInput!
        $sortBy: SortByInput
        $searchText: String
    ) {
        portfolio {
            getSiteTable(
                customerId: $customerId
                metric: $metric
                filters: $filters
                pagination: $pagination
                sortBy: $sortBy
                searchText: $searchText
            ) {
                ${SITE_TABLE_FIELDS}
            }
        }
    }
`;

// Grid-backed twin of siteTableQuery. Also the only one of the three whose species
// columns are sortable, because core merges the counts before it picks the page.
export const nextSiteTableQuery = gql`
    query getNextSiteTable(
        $customerId: String!
        $metric: HlcaMetricType
        $filters: [CustomFilterOptionInput]
        $pagination: PaginationInput!
        $sortBy: SortByInput
        $searchText: String
    ) {
        portfolio {
            getNextSiteTable(
                customerId: $customerId
                metric: $metric
                filters: $filters
                pagination: $pagination
                sortBy: $sortBy
                searchText: $searchText
            ) {
                ${SITE_TABLE_FIELDS}
            }
        }
    }
`;

export type NextSiteTableQueryResult = {
    portfolio: {
        getNextSiteTable: SiteTableResponse;
    };
};

export type SiteTableRow = {
    id: string;
    name: string;
    area: number;
    country: string;
    metricValue: number | null;
    metrics: PortfolioSiteMetric[];
    speciesRedListCount: number | null;
    speciesInvasiveCount: number | null;
    hasEdna: boolean;
    hasHabitatInsights: boolean;
};

export type SiteTableResponse = {
    rows: SiteTableRow[];
    pagination: {
        page: number;
        pageSize: number;
        total: number;
    };
};

export type SiteTableQueryResult = {
    portfolio: {
        getSiteTable: SiteTableResponse;
    };
};

const SITE_POPUP_FIELDS = `
    id
    name
    totalArea
    country
    latestYear
    metrics {
        name
        label
        frameworks
        ... on MultiMetricSingleYearSiteMetric {
            year
            metrics
        }
        ... on SingleYearSiteMetric {
            year
            data
        }
        ... on MultiYearSiteMetric {
            years
            datum
        }
        ... on MultiYearMetricSingleYearSiteMetric {
            year
            years
            metricWithTrend
        }
    }
    speciesRedListCount
    speciesInvasiveCount
    speciesTotalCount
`;

export const sitePopupQuery = gql`
    query getSitePopup($customerId: String!, $siteId: String!) {
        portfolio {
            getSitePopup(customerId: $customerId, siteId: $siteId) {
                ${SITE_POPUP_FIELDS}
            }
        }
    }
`;

// Grid-backed twin of sitePopupQuery. The only one of the three carrying a species
// total as well as the two category counts.
export const nextSitePopupQuery = gql`
    query getNextSitePopup($customerId: String!, $siteId: String!) {
        portfolio {
            getNextSitePopup(customerId: $customerId, siteId: $siteId) {
                ${SITE_POPUP_FIELDS}
            }
        }
    }
`;

export type NextSitePopupQueryResult = {
    portfolio: {
        getNextSitePopup: SitePopupData;
    };
};

export type SitePopupData = {
    id: string;
    name: string;
    totalArea: number;
    country: string;
    latestYear: number | null;
    metrics: PortfolioSiteMetric[];
    speciesRedListCount: number;
    speciesInvasiveCount: number;
    speciesTotalCount: number;
};

export type SitePopupQueryResult = {
    portfolio: {
        getSitePopup: SitePopupData;
    };
};

export const sitePolygonsQuery = gql`
    query getSitePolygons($customerId: String!, $siteId: String!) {
        portfolio {
            getSitePolygons(customerId: $customerId, siteId: $siteId) {
                name
                coordinates
            }
        }
    }
`;

export type SitePolygonData = {
    name: string;
    coordinates: number[][][][];
};

export type SitePolygonsQueryResult = {
    portfolio: {
        getSitePolygons: SitePolygonData[];
    };
};

const SITE_SPECIES_ASSESSMENT_FIELDS = `
    scientificName
    commonName
    iucnRedListCategory
    predictionLevel
    hasEDNAData
    hasUploadedData
    foundInEDNA
    foundInUploadedData
    isNew
`;

// Dedicated site species query for the site species risk modal
export const siteSpeciesRiskQuery = gql`
    query getSiteSpeciesRisk($siteId: String!, $filters: [CustomFilterOptionInput]) {
        siteSummary {
            getSiteSpeciesRisk(siteId: $siteId, filters: $filters) {
                snapshot
                siteName
                country
                area
                redListSpecies {
                    ${SITE_SPECIES_ASSESSMENT_FIELDS}
                }
                invasiveSpecies {
                    ${SITE_SPECIES_ASSESSMENT_FIELDS}
                }
            }
        }
    }
`;

export type SiteSpeciesAssessmentItem = {
    scientificName: string;
    commonName?: string | null;
    iucnRedListCategory: string;
    predictionLevel: string;
    hasEDNAData: boolean;
    hasUploadedData: boolean;
    foundInEDNA: boolean;
    foundInUploadedData: boolean;
    /** First appeared at this site in the served snapshot (DD-6012). */
    isNew?: boolean;
};

export type SiteSpeciesRiskData = {
    snapshot: string;
    siteName: string;
    country: string;
    area: number;
    redListSpecies: SiteSpeciesAssessmentItem[];
    invasiveSpecies: SiteSpeciesAssessmentItem[];
};

export type SiteSpeciesRiskQueryResult = {
    siteSummary: {
        getSiteSpeciesRisk: SiteSpeciesRiskData;
    };
};
